import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Ban, Copy, Check, Eye, EyeOff } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { useRevokeApiKey } from '@/lib/api/apiKeys';
import { useToast } from '@/components/ui/use-toast';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import type { ApiKey } from '@/lib/api/apiKeys';

interface AccessTokensTableProps {
  data: ApiKey[];
  pageCount: number;
  page: number;
  onPageChange: (page: number) => void;
}

function TokenCell({ token, t }: { token: string; t: (key: string) => string }) {
  const [revealed, setRevealed] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const { toast } = useToast();

  const maskedToken = `****...${token.slice(-4)}`;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      toast({
        title: t('accessTokens.table.toast.copiedTitle'),
        description: t('accessTokens.table.toast.copiedDescription'),
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: t('accessTokens.table.toast.copyFailedTitle'),
        description: t('accessTokens.table.toast.copyFailedDescription'),
        variant: 'destructive',
      });
    }
  };

  const handleReveal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRevealed(!revealed);
  };

  return (
    <div className="flex items-center gap-2">
      <code className="bg-muted rounded px-2 py-1 font-mono text-xs">
        {revealed ? token : maskedToken}
      </code>
      <Button variant="ghost" size="sm" onClick={handleReveal} className="h-7 w-7 p-0">
        {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </Button>
      <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 w-7 p-0">
        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

export function AccessTokensTable({ data, pageCount, page, onPageChange }: AccessTokensTableProps) {
  const revokeMutation = useRevokeApiKey();
  const { t, i18n } = useTranslation('settings');
  const [revokeTarget, setRevokeTarget] = React.useState<ApiKey | null>(null);

  const handleConfirmRevoke = () => {
    if (!revokeTarget) return;
    revokeMutation.mutate(
      { id: revokeTarget.id },
      { onSettled: () => setRevokeTarget(null) },
    );
  };

  const columns: ColumnDef<ApiKey>[] = React.useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('accessTokens.table.columns.name'),
        cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
      },
      {
        accessorKey: 'token',
        header: t('accessTokens.table.columns.apiKey'),
        cell: ({ row }) => <TokenCell token={row.original.token} t={t} />,
      },
      {
        accessorKey: 'description',
        header: t('accessTokens.table.columns.description'),
        cell: ({ row }) => (
          <div className="text-muted-foreground text-sm">{row.original.description || '—'}</div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: t('accessTokens.table.columns.created'),
        cell: ({ row }) => {
          const date = new Date(row.original.createdAt);
          return (
            <div className="flex flex-col gap-0.5">
              <div className="text-sm tabular-nums">{format(date, 'MMM dd, yyyy')}</div>
              <div className="text-muted-foreground text-xs tabular-nums">{format(date, 'HH:mm')}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'expiresAt',
        header: t('accessTokens.table.columns.expires'),
        cell: ({ row }) => {
          const expiresAt = row.original.expiresAt;
          if (!expiresAt) return <span className="text-muted-foreground">{t('accessTokens.table.expires.never')}</span>;
          const date = new Date(expiresAt);
          return (
            <div className="flex flex-col gap-0.5">
              <div className="text-sm tabular-nums">{format(date, 'MMM dd, yyyy')}</div>
              <div className="text-muted-foreground text-xs tabular-nums">{format(date, 'HH:mm')}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'revokedAt',
        header: t('accessTokens.table.columns.status'),
        cell: ({ row }) => {
          const isRevoked = !!row.original.revokedAt;
          return (
            <Badge variant={isRevoked ? 'destructive' : 'green'}>
              {isRevoked ? t('accessTokens.table.status.revoked') : t('accessTokens.table.status.active')}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const token = row.original;
          const isRevoked = !!token.revokedAt;

          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!isRevoked && (
                    <DropdownMenuItem
                      onClick={() => setRevokeTarget(token)}
                      className="text-destructive"
                    >
                      <Ban className="mr-2 h-4 w-4" />
                      {t('accessTokens.table.actions.revokeToken')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [t, i18n.language],
  );

  const { table } = useDataTable({
    data,
    columns,
    total: data.length,
    config: {
      skeletonRows: 5,
    },
    onTableStateChange: () => {},
    getRowId: (row: ApiKey) => row.id,
  });

  return (
    <>
      <DataTable
        key={i18n.language}
        table={table}
        isLoading={false}
        showFilters={false}
        showToolbar={false}
        showPagination={false}
      />

      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accessTokens.revokeDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('accessTokens.revokeDialog.description', { name: revokeTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('accessTokens.revokeDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('accessTokens.revokeDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
